"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { createRef, FC, useEffect, useMemo, useRef, useState } from "react";

import { SIH_URL } from "@/utils";

import { LastNodeMessage } from "../last-node-message";

import { ControlButtons, GridProfileCard } from "./components";
import styles from "./grid-cards.module.scss";
import { GridCardsProps } from "./grid-cards.types";

const GridCards: FC<GridCardsProps> = ({
  members,
  hasNextPage,
  isFetchingNextPage,
  withoutLastNodeMessage,
  currentIndex,
  markViewing = false,
  fetchNextPage,
  changeIndex,
}) => {
  const { push } = useRouter();

  const [viewedUserIds, setViewedUserIds] = useState<string[]>([]);

  const observer = useRef<IntersectionObserver | null>(null);
  const cardRefs = useMemo(() => members.map(() => createRef<HTMLDivElement>()), [members]);

  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= members.length) return;

    if (cardRefs?.[currentIndex]?.current) {
      cardRefs?.[currentIndex]?.current?.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    }
  }, []);

  useEffect(() => {
    const currentUserId = members[currentIndex]?.id;

    if (markViewing && currentUserId) {
      const isUserFound = viewedUserIds.find((userId) => userId === currentUserId);

      if (!isUserFound) {
        setViewedUserIds([...viewedUserIds, currentUserId]);
      }
    }

    if (currentIndex === 0 && cardRefs[0]?.current) {
      cardRefs[0].current.scrollIntoView();
    }
  }, [currentIndex]);

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const visibleCardIndex = cardRefs.findIndex((ref) => ref.current === entry.target);

            if (visibleCardIndex !== -1) {
              changeIndex(visibleCardIndex);
            }

            if (visibleCardIndex === cardRefs.length - 6 && hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }
        });
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    cardRefs.forEach((ref) => {
      if (ref.current) {
        observer.current?.observe(ref.current);
      }
    });

    return () => {
      observer.current?.disconnect();
    };
  }, [cardRefs]);

  return (
    <div className={styles.container}>
      <div className={styles.gridCards}>
        {members.map(({ id, profileImage, admiredByMe, ...profile }, index) => (
          <div
            key={id}
            ref={cardRefs[index]}
            role="button"
            tabIndex={0}
            className={styles.card}
            onClick={() => push(`/platform/profile/${id}`)}
          >
            <Image alt="member" src={SIH_URL({ key: profileImage?.path })} layout="fill" />

            <div className={styles.preview}>
              <GridProfileCard id={id} profileImage={profileImage} {...profile} />

              <ControlButtons {...{ id, admiredByMe }} />
            </div>
          </div>
        ))}

        {!hasNextPage && !withoutLastNodeMessage && <div className={styles.lastNode}>{<LastNodeMessage />}</div>}
      </div>
    </div>
  );
};

export { GridCards };
